import { useMemo, useState, useRef, useEffect } from 'react';
import { Phone, MessageSquare, Mail, Radio, Loader2, Star, EyeOff, Eye, Plus, X, Trash2, ExternalLink, MessageCircle } from 'lucide-react';
import { WhatsAppIcon } from '@/components/icons/whatsapp-icon';
import { InstagramIcon } from '@/components/icons/instagram-icon';
import { TelegramIcon } from '@/components/icons/telegram-icon';
import { cn } from '@/lib/utils';
import { cleanSenderLabel } from '@/lib/utils/format';
import { Select } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useSenders } from '@/hooks/use-senders';
import { useMessageStore } from '@/stores/message-store';
import { useSettingsStore } from '@/stores/settings-store';
import { useAppStore } from '@/stores/app-store';
import { useModuleStore } from '@/stores/module-store';
import type { Channel } from '@/types/messaging';

const CHANNEL_ICONS: Record<Channel, React.ComponentType<{ className?: string }>> = {
  SMS: MessageSquare,
  MMS: MessageSquare,
  WHATSAPP: WhatsAppIcon,
  VOICE: Radio,
  EMAIL: Mail,
  INTERCOM: MessageCircle,
  INSTAGRAM: InstagramIcon,
  TELEGRAM: TelegramIcon,
};

const CHANNEL_COLORS: Record<Channel, string> = {
  SMS: 'text-blue-500',
  MMS: 'text-blue-500',
  WHATSAPP: 'text-green-500',
  VOICE: 'text-orange-500',
  EMAIL: 'text-purple-500',
  INTERCOM: 'text-blue-600',
  INSTAGRAM: 'text-pink-500',
  TELEGRAM: 'text-sky-500',
};

type ViewContext = 'compose' | 'inbox' | 'chat';

interface SenderPickerProps {
  viewContext?: ViewContext;
}

export function SenderPicker({ viewContext = 'compose' }: SenderPickerProps) {
  const { senders, hiddenSenders, loading, error } = useSenders(viewContext);
  const sender = useMessageStore((s) => s.sender);
  const setSender = useMessageStore((s) => s.setSender);
  const preferences = useSettingsStore((s) => s.preferences);
  const updatePreferences = useSettingsStore((s) => s.updatePreferences);
  const tpaModule = useModuleStore((s) => s.tpaModule);
  // TPAs whose provider has no sender-listing endpoint (Kaleyra, BulkSMS,
  // Clickatell, Grapevine, AnySMS) need the compose-view picker to always
  // show "+ Add" so users can enter their portal-configured sender ID.
  const tpaHasSenderFetch = tpaModule?.hasSenderFetchAPI?.() ?? true;

  const [showHidden, setShowHidden] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ senderId: string; x: number; y: number } | null>(null);
  const contextRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!contextMenu) return;
    const handler = (e: MouseEvent) => {
      if (contextRef.current && !contextRef.current.contains(e.target as Node)) {
        setContextMenu(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [contextMenu]);

  const hideSenderPicker = preferences.hideSenderPicker;

  const getDefaultSenderId = () => {
    if (viewContext === 'inbox') return preferences.inboxDefaultSender?.id;
    if (viewContext === 'chat') return preferences.chatDefaultSender?.id;
    return preferences.composeDefaultSender?.id;
  };

  const defaultSenderKey = viewContext === 'inbox'
    ? 'inboxDefaultSender' as const
    : viewContext === 'chat'
      ? 'chatDefaultSender' as const
      : 'composeDefaultSender' as const;

  const handleSetDefault = (senderId: string) => {
    const s = [...senders, ...hiddenSenders].find((x) => x.id === senderId);
    if (!s) return;
    updatePreferences({
      [defaultSenderKey]: { id: s.id, label: s.label, channel: s.channel, phoneNumber: s.phoneNumber },
    });
    // Also select this sender as the active sender
    setSender(s);
    setContextMenu(null);
  };

  const handleClearDefault = () => {
    updatePreferences({ [defaultSenderKey]: undefined });
    setContextMenu(null);
  };

  const getHiddenSenderKey = () => {
    if (viewContext === 'inbox') return 'inboxHiddenSenderIds' as const;
    return 'hiddenSenderIds' as const;
  };

  const handleHideSender = (senderId: string) => {
    const key = getHiddenSenderKey();
    const current = preferences[key] || [];
    updatePreferences({ [key]: [...current, senderId] });
    setContextMenu(null);
  };

  const handleUnhideSender = (senderId: string) => {
    const key = getHiddenSenderKey();
    const current = preferences[key] || [];
    updatePreferences({ [key]: current.filter((id) => id !== senderId) });
  };

  const handleDeleteCustomSender = (customId: string) => {
    const current = preferences.customSenders || [];
    updatePreferences({ customSenders: current.filter((cs) => cs.id !== customId) });
    const key = getHiddenSenderKey();
    const hiddenList = preferences[key] || [];
    const fullId = `custom_${customId}`;
    if (hiddenList.includes(fullId)) {
      updatePreferences({ [key]: hiddenList.filter((id) => id !== fullId) });
    }
    setContextMenu(null);
  };

  const displaySenders = showHidden ? [...senders, ...hiddenSenders] : senders;

  const options = useMemo(
    () =>
      displaySenders.map((s) => {
        const Icon = CHANNEL_ICONS[s.channel] || Phone;
        return {
          value: s.id,
          label: cleanSenderLabel(s.label),
          description: s.phoneNumber || s.channel,
          icon: <Icon className={cn('h-4 w-4', CHANNEL_COLORS[s.channel])} />,
        };
      }),
    [displaySenders]
  );

  // Auto-select single sender — MUST be before early returns to avoid conditional hook calls
  const singleSender = displaySenders.length === 1 ? displaySenders[0] : null;
  useEffect(() => {
    if (singleSender && (!sender || sender.id !== singleSender.id)) {
      setSender(singleSender);
    }
  }, [singleSender, sender, setSender]);

  if (hideSenderPicker) return null;

  if (loading) {
    return (
      <div className="space-y-2">
        <Label className="text-sm font-semibold text-primary">From</Label>
        <Skeleton className="h-10 w-full rounded-md" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-2">
        <Label className="text-sm font-semibold text-primary">From</Label>
        <p className="text-sm text-destructive">Failed to load senders: {String(error)}</p>
      </div>
    );
  }

  const isDefault = (id: string) => getDefaultSenderId() === id;
  const isHidden = (id: string) => (preferences[getHiddenSenderKey()] || []).includes(id);
  const isCustom = (id: string) => id.startsWith('custom_');

  // Single sender — show static display instead of dropdown
  if (singleSender) {
    const Icon = CHANNEL_ICONS[singleSender.channel] || Phone;
    return (
      <div className="space-y-2">
        <Label className="text-sm font-semibold text-primary">From</Label>
        <div data-testid="sender-picker" className="flex items-center gap-2.5 rounded-md border border-input bg-background px-3 py-2">
          <Icon className={cn('h-4 w-4 shrink-0', CHANNEL_COLORS[singleSender.channel])} />
          <div className="flex flex-col min-w-0 flex-1">
            <span className="text-sm font-medium truncate">{cleanSenderLabel(singleSender.label)}</span>
            {singleSender.phoneNumber && (
              <span className="text-xs text-muted-foreground truncate">{singleSender.phoneNumber}</span>
            )}
          </div>
          <span className="shrink-0 text-[10px] font-medium text-muted-foreground uppercase">
            {singleSender.channel}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold text-primary">From</Label>
        <div className="flex items-center gap-2">
          {hiddenSenders.length > 0 && (
            <button
              type="button"
              onClick={() => setShowHidden(!showHidden)}
              className="text-[11px] text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
            >
              {showHidden ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
              {showHidden ? 'Hide hidden' : `${hiddenSenders.length} hidden`}
            </button>
          )}
          {/* Show + Add when:
              - in settings (always),
              - in compose and no senders are available, or
              - the TPA has no sender-fetch API so the user must supply
                the sender ID themselves (even if some placeholders exist). */}
          {(viewContext !== 'compose' || displaySenders.length === 0 || !tpaHasSenderFetch) && (
            <button
              type="button"
              onClick={() => setShowAddForm(!showAddForm)}
              className="text-[11px] text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
            >
              <Plus className="h-3 w-3" />
              Add
            </button>
          )}
        </div>
      </div>

      {/* For TPAs without a sender-fetch API, an empty picker is the
          steady-state post-auth: the user *has* to add their sender ID.
          Replace the Select trigger itself with an "Add sender" CTA so
          there's no confusing empty dropdown to open. */}
      {displaySenders.length === 0 && !loading && !tpaHasSenderFetch ? (
        <button
          type="button"
          data-testid="sender-picker"
          onClick={() => setShowAddForm(true)}
          className="flex w-full items-center justify-between gap-2 rounded-md border border-dashed border-primary/40 bg-primary/5 px-3 py-2.5 text-sm font-medium text-primary hover:bg-primary/10 hover:border-primary/60 transition-colors"
        >
          <span className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Add sender
          </span>
          <span className="text-[11px] font-normal text-muted-foreground">
            No sender-list API
          </span>
        </button>
      ) : (
      <div data-testid="sender-picker">
      <Select
        value={sender?.id || ''}
        onValueChange={(value) => {
          const selected = displaySenders.find((s) => s.id === value);
          if (selected) setSender(selected);
        }}
        options={options}
        placeholder="Select a sender..."
        searchable={displaySenders.length > 5}
        emptyText="No senders available — click + Add to enter one"
        renderOption={(option, isSelected) => {
          const senderData = displaySenders.find((s) => s.id === option.value);
          const Icon = senderData ? CHANNEL_ICONS[senderData.channel] : Phone;
          const colorClass = senderData ? CHANNEL_COLORS[senderData.channel] : '';
          const hidden = senderData ? isHidden(senderData.id) : false;
          const defaultSender = senderData ? isDefault(senderData.id) : false;

          return (
            <div
              className={cn('flex items-center gap-2.5 flex-1 min-w-0', hidden && 'opacity-50')}
              onContextMenu={(e) => {
                if (!senderData) return;
                e.preventDefault();
                setContextMenu({ senderId: senderData.id, x: e.clientX, y: e.clientY });
              }}
            >
              <Icon className={cn('h-4 w-4 shrink-0', colorClass)} />
              <div className="flex flex-col items-start min-w-0 flex-1 overflow-hidden text-left">
                <span className="w-full text-sm truncate">
                  {option.label}
                  {hidden && <EyeOff className="inline h-3 w-3 text-muted-foreground ml-1" />}
                </span>
                {option.description && (
                  <span className="w-full text-xs text-muted-foreground truncate">{option.description}</span>
                )}
              </div>
              {senderData && (
                <span className="shrink-0 text-[10px] font-medium text-muted-foreground uppercase">
                  {senderData.channel}
                </span>
              )}
              {senderData && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    defaultSender ? handleClearDefault() : handleSetDefault(senderData.id);
                  }}
                  className={cn(
                    'shrink-0 p-1 rounded transition-colors cursor-pointer',
                    defaultSender
                      ? 'text-amber-500'
                      : 'text-muted-foreground/20 hover:text-amber-500'
                  )}
                  title={defaultSender ? 'Remove default' : 'Set as default'}
                >
                  <Star className={cn('h-3 w-3', defaultSender && 'fill-amber-500')} />
                </span>
              )}
            </div>
          );
        }}
      />
      </div>
      )}

      {/* Inline empty-state CTA — surfaced below the Select for TPAs that
          DO have a sender-fetch API but happen to have returned zero
          results (e.g. a freshly-provisioned Twilio account). The no-API
          branch above already replaces the trigger with an Add button,
          so this CTA is suppressed there to avoid redundancy. */}
      {displaySenders.length === 0 && !loading && !showAddForm && tpaHasSenderFetch && (
        <button
          type="button"
          onClick={() => setShowAddForm(true)}
          className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground hover:border-primary/40 transition-colors"
        >
          <Plus className="h-3.5 w-3.5" />
          Add a sender to get started
        </button>
      )}

      {/* Context menu */}
      {contextMenu && (
        <div
          ref={contextRef}
          className="fixed z-[9999] rounded-md border border-border bg-popover py-1 shadow-md min-w-[160px]"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          {isDefault(contextMenu.senderId) ? (
            <button onClick={handleClearDefault} className="flex w-full items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted transition-colors">
              <Star className="h-3.5 w-3.5" /> Remove default
            </button>
          ) : (
            <button onClick={() => handleSetDefault(contextMenu.senderId)} className="flex w-full items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted transition-colors">
              <Star className="h-3.5 w-3.5 text-amber-500" /> Set as default
            </button>
          )}
          {isHidden(contextMenu.senderId) ? (
            <button onClick={() => { handleUnhideSender(contextMenu.senderId); setContextMenu(null); }} className="flex w-full items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted transition-colors">
              <Eye className="h-3.5 w-3.5" /> Unhide
            </button>
          ) : (
            <button onClick={() => handleHideSender(contextMenu.senderId)} className="flex w-full items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted transition-colors">
              <EyeOff className="h-3.5 w-3.5" /> Hide
            </button>
          )}
          {isCustom(contextMenu.senderId) && (
            <>
              <div className="my-1 border-t border-border" />
              <button onClick={() => { handleDeleteCustomSender(contextMenu.senderId.replace('custom_', '')); }} className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-destructive hover:bg-destructive/10 transition-colors">
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            </>
          )}
        </div>
      )}

      {showAddForm && (viewContext !== 'compose' || displaySenders.length === 0 || !tpaHasSenderFetch) && <AddCustomSenderForm onClose={() => setShowAddForm(false)} />}

      {/* Channel/phone hint removed — sender dropdown already shows this info */}
    </div>
  );
}

function AddCustomSenderForm({ onClose }: { onClose: () => void }) {
  const [label, setLabel] = useState('');
  const [senderId, setSenderId] = useState('');
  const [channel, setChannel] = useState<'SMS' | 'WHATSAPP'>('SMS');
  const preferences = useSettingsStore((s) => s.preferences);
  const updatePreferences = useSettingsStore((s) => s.updatePreferences);
  const appCodeTPA = useAppStore((s) => s.appCodeTPA);

  const isTwilioWhatsApp = appCodeTPA === 'twilio' && channel === 'WHATSAPP';

  const handleSave = () => {
    if (!senderId.trim()) return;
    const id = senderId.trim().replace(/[^A-Za-z0-9+]/g, '_').slice(0, 100);
    const newSender = { id, label: label.trim() || senderId.trim(), senderId: senderId.trim(), channel, createdAt: Date.now() };
    const existing = preferences.customSenders || [];
    updatePreferences({ customSenders: [...existing, newSender] });
    onClose();
  };

  return (
    <div className="rounded-md border border-border bg-muted/30 p-3 space-y-3 mt-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium">Add Custom Sender</p>
        <button type="button" onClick={onClose} className="p-0.5 hover:bg-muted rounded transition-colors">
          <X className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] font-medium text-muted-foreground">Channel</label>
          <select value={channel} onChange={(e) => setChannel(e.target.value as 'SMS' | 'WHATSAPP')} className="mt-1 w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
            <option value="SMS">SMS</option>
            <option value="WHATSAPP">WhatsApp</option>
          </select>
        </div>
        {!isTwilioWhatsApp && (
          <div>
            <label className="text-[10px] font-medium text-muted-foreground">Sender ID</label>
            <input type="text" value={senderId} onChange={(e) => setSenderId(e.target.value)} placeholder="+1234567890" className="mt-1 w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
        )}
      </div>

      {isTwilioWhatsApp ? (
        <div className="rounded-lg border border-green-500/30 bg-green-500/5 p-3 space-y-2.5">
          <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
            <MessageCircle className="h-4 w-4 shrink-0" />
            <p className="text-xs font-medium">WhatsApp senders are managed in Twilio</p>
          </div>
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            WhatsApp senders need to be configured via the Twilio console. Go to <strong>Messaging &rarr; Senders &rarr; WhatsApp senders</strong> to add or manage your WhatsApp-enabled numbers.
          </p>
          <div className="flex items-center gap-2 pt-0.5">
            <button
              type="button"
              onClick={() => window.open('https://console.twilio.com/us1/develop/sms/senders/whatsapp-senders', '_blank')}
              className="inline-flex items-center gap-1.5 rounded-md bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700 transition-colors"
            >
              <ExternalLink className="h-3 w-3" />
              Open Twilio Console
            </button>
            <button type="button" onClick={onClose} className="rounded-md px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted transition-colors">Close</button>
          </div>
        </div>
      ) : (
        <>
          <div>
            <label className="text-[10px] font-medium text-muted-foreground">Display Name (optional)</label>
            <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Marketing Number" className="mt-1 w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-md px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted transition-colors">Cancel</button>
            <button type="button" onClick={handleSave} disabled={!senderId.trim()} className="rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors">Add</button>
          </div>
        </>
      )}
    </div>
  );
}
